import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { authAPI } from '../../services/api';
import { marathiPolicies, marathiUi } from './LegalPage.mr';
import { phoneHref } from '../../utils/phone';
import './LegalPage.css';

export const LEGAL_VERSION = '1.0';
export const LEGAL_EFFECTIVE_DATE = '28 July 2026';

export const policies = {
  terms: {
    title: 'Terms and Conditions of Use',
    summary: 'Rules governing access to this Gram Panchayat digital service.',
    sections: [
      ['1. Service owner and acceptance', [
        'This portal is an official digital service made available by the Gram Panchayat identified on this page. By registering, signing in, submitting an application, making a payment, or otherwise using the portal, you agree to these Terms, the Privacy Policy, and the service-specific instructions shown during a transaction.',
        'If you act for a business, household, minor, or another person, you confirm that you have lawful authority to do so and must provide evidence when requested.',
      ]],
      ['2. Eligibility, identity and account security', [
        'You must provide accurate, current, and complete information. Registration or linking to a citizen, household, property, tax, or business record is subject to official verification and does not itself create ownership, residence, licence, benefit, or other legal status.',
        'Keep credentials and OTPs confidential. You are responsible for activity through your account until you report suspected compromise. Impersonation, false declarations, forged documents, credential sharing, and unauthorised access are prohibited and may be reported to competent authorities.',
      ]],
      ['3. Permissions and authorised use', [
        'Access is role-, membership-, tenant-, and purpose-based. Permissions may include viewing records, applying for services, uploading documents, paying dues, downloading receipts, managing authorised profiles, or performing official administrative work.',
        'A displayed permission is not proof of legal entitlement. The Gram Panchayat may restrict, suspend, revoke, or correct access when authority expires, employment or representation changes, records conflict, misuse is suspected, or law requires it. Users must not attempt to bypass controls, inspect another person’s data, scrape records, reverse engineer security, introduce malware, overload the service, or use information for harassment, profiling, advertising, or resale.',
      ]],
      ['4. Applications, records, payments and communications', [
        'Online submissions are requests for official processing. Approval, certificate issuance, mutation, licence, tax assessment, benefit, complaint disposal, or registry change occurs only after the competent authority completes the applicable statutory and administrative process.',
        'Payment status is subject to confirmation by the payment provider and government accounts. Preserve the official receipt and report discrepancies promptly. Statutory fees, taxes, penalties, refunds, and appeal rights are governed by applicable law and official orders.',
        'You consent to service communications necessary to operate your account or transaction, including OTPs, status updates, deficiency notices, receipts, security alerts, and legally required notices by portal, SMS, WhatsApp, email, post, or telephone. Promotional messages require a separate choice where applicable.',
      ]],
      ['5. Accuracy, availability and external services', [
        'Reasonable efforts are made to keep information accurate and available. In case of conflict, the signed order, statutory register, official receipt, gazette, government resolution, or record held by the competent authority prevails.',
        'Maintenance, connectivity, disasters, cyber incidents, third-party networks, payment gateways, maps, messaging services, or lawful directions may interrupt service. External links are provided for convenience and are governed by the destination’s policies.',
      ]],
      ['6. Intellectual property and evidence', [
        'Government content may be used only as permitted by the Copyright Policy and applicable law. Logos, emblems, seals, certificates, signatures, and security marks must not be altered or used to imply approval.',
        'Electronic submissions, acknowledgements, audit trails, timestamps, device/network records, and consent records may be retained and produced as evidence as permitted by law.',
      ]],
      ['7. Suspension, changes, governing law and contact', [
        'The service may suspend an account or transaction to protect citizens, public records, revenue, or system security. Where legally required and practicable, notice and a review channel will be provided.',
        'These Terms may be updated for legal, security, or service changes. Material changes will be notified and fresh acceptance may be requested. Indian law applies. Statutory grievance, appeal, RTI, consumer, data-protection, and court remedies remain unaffected; jurisdiction will be determined under applicable law.',
      ]],
    ],
  },
  privacy: {
    title: 'Privacy and Personal Data Protection Policy',
    summary: 'How personal data is collected, used, shared, protected, retained, and corrected.',
    sections: [
      ['1. Data fiduciary and scope', [
        'The Gram Panchayat identified on this page is responsible for personal data processed for its public functions and citizen services, subject to allocation of responsibility with authorised government departments, technology providers, and statutory authorities.',
        'This policy applies to portal registration, authentication, household and citizen services, property and tax services, business services, complaints, certificates, payments, communications, logs, and support.',
      ]],
      ['2. Personal data we may process', [
        'Identity and contact data: name, username, email, mobile number, address, date of birth, gender, identifiers and verification documents. Household and relationship data: household membership and relationship claims. Service data: applications, certificates, complaints, uploaded evidence, schemes, property interests, business authority, tax accounts, payments, receipts, and official decisions.',
        'Technical and security data: IP address, device/browser information, timestamps, authentication events, consent/version records, audit logs, diagnostics, and security events. Location is processed only when submitted for a service or explicitly permitted for a location feature.',
        'Do not submit personal data that is unnecessary for the stated service. Aadhaar or other identity documents must be collected, used, displayed, and retained only where an authorised lawful basis and approved procedure exist; numbers should be masked wherever full display is unnecessary.',
      ]],
      ['3. Purposes and lawful processing', [
        'Data is processed to verify identity and authority; create and secure accounts; link official records; provide statutory and administrative services; calculate and collect dues; issue acknowledgements and records; investigate complaints; prevent fraud; maintain auditability; respond to lawful requests; ensure cybersecurity; and comply with legal obligations.',
        'Depending on the service, processing may be based on your consent, your voluntary request for a specified service, performance of a State function, compliance with law or court/order, protection from fraud or security threats, or another ground permitted by applicable law. Consent may be withdrawn where consent is the applicable basis; withdrawal does not invalidate prior lawful processing or processing required under another legal basis.',
      ]],
      ['4. Permissions, access and sharing', [
        'Access is limited by tenant, role, membership, assigned work, and approved purpose. Authorised Gram Panchayat staff, supervisory government authorities, auditors, payment providers, messaging providers, hosting/security vendors, and other processors may receive only the data needed for their function and must be bound by applicable confidentiality, security, and purpose restrictions.',
        'Data may be disclosed when required by law, court, police, audit, tax, anti-fraud, CERT-In, or another competent authority. Public disclosure and RTI responses will be assessed under applicable exemptions and third-party protections. Personal data is not sold or used for commercial advertising.',
        'Cross-border processing will occur only when permitted by the Government of India and subject to applicable safeguards. Approved vendors must not independently reuse citizen data.',
      ]],
      ['5. Retention and deletion', [
        'Data is retained only for the service, statutory record schedule, audit, limitation, dispute, financial, archival, and security periods applicable to the record. Security logs may be retained for at least the period required by CERT-In directions. Records subject to litigation hold, audit, investigation, RTI, or statutory archives will not be erased until release is lawful.',
        'After the applicable period, data will be securely deleted, anonymised, or transferred to an authorised archive. Account closure does not automatically delete official public records or transactions that the authority must preserve.',
      ]],
      ['6. Security and breach response', [
        'Controls include authenticated access, least privilege, tenant isolation, encryption in transit, credential hashing, secure document access, logging, backups, vulnerability management, incident response, and vendor controls. No online service can promise absolute security.',
        'Suspected compromise should be reported immediately. The authority will contain, assess, document, and notify affected persons and competent authorities where applicable law requires.',
      ]],
      ['7. Your rights and duties', [
        'Subject to applicable law, you may request information about processing, access a summary of personal data and recipients, correction or completion, erasure where retention is no longer lawful, grievance redressal, withdrawal of consent, and nomination of another person to exercise rights in specified circumstances.',
        'Requests require identity verification and may be refused or limited where an exemption, another person’s rights, official-record integrity, investigation, legal obligation, or retention duty applies. You must provide authentic information, not impersonate others, and not file false or frivolous grievances.',
      ]],
      ['8. Children and persons requiring assistance', [
        'A parent or lawful guardian must act for a child or person who cannot provide legally valid consent where required. The portal must not conduct tracking, behavioural monitoring, or targeted advertising directed at children. Age and guardianship claims may be verified.',
      ]],
      ['9. Grievance and escalation', [
        'Send privacy requests or grievances to the official contact shown below with your name, contact information, service/reference number, requested action, and sufficient identity proof. Do not send full Aadhaar numbers or passwords by email.',
        'Citizens may contact the designated Grievance Officer and Data Protection Officer at the Gram Panchayat administrative office or via the official grievance portal. If unresolved, remedies available through the applicable supervisory authority, Data Protection Board, department, statutory appeal, or court remain available.',
      ]],
    ],
  },
  refund: {
    title: 'Refund and Cancellation Policy',
    summary: 'Rules governing online payments for property taxes, civic service fees, and certificate charges.',
    sections: [
      ['1. Payment nature and statutory fees', [
        'Payments made on this portal or connected mobile applications are for municipal taxes (such as Property Tax, Water Tax, Business Tax) and official government service/certificate application charges levied by the Gram Panchayat pursuant to the Maharashtra Village Panchayats Act.',
        'Payment status is confirmed upon electronic acknowledgement and issuance of an official computerized receipt with a unique transaction reference number (UTR/Challan ID).',
      ]],
      ['2. Double payments and duplicate debits', [
        'If an amount is debited multiple times from a citizen’s bank account or payment instrument for the same transaction due to a network or technical failure, the excess amount will be verified against the payment gateway settlement.',
        'Upon administrative reconciliation, verified duplicate payments will either be refunded to the original payment source within 7-10 working days or credited/adjusted against the taxpayer’s ledger balance upon citizen request.',
      ]],
      ['3. Non-refundable statutory fees and cancellation', [
        'Application processing fees for certificates and civic service requests become non-refundable once administrative verification, field inquiry, or document processing has commenced by the Gram Panchayat office.',
        'Tax payments successfully credited to the Gram Panchayat treasury cannot be cancelled online. Any adjustment, reassessment, or refund of statutory taxes must follow the official appeal and rectification procedure before the competent authority.',
      ]],
      ['4. Dispute resolution and grievance', [
        'In case of payment discrepancies, missing receipts, or technical deduction errors, citizens should contact the Gram Panchayat administration with the transaction reference, date, and bank statement excerpt at the contact details provided below.',
      ]],
    ],
  },
  'data-deletion': {
    title: 'User Account and Data Deletion Policy',
    summary: 'Procedures for deleting user accounts, data retention exceptions, and online erasure requests.',
    sections: [
      ['1. In-App and Online Account Deletion', [
        'Registered citizens can request complete deactivation and deletion of their user account at any time either through the mobile application (via Profile → Security / Account → Delete Account) or through the online deletion request form on this page.',
        'Deleting an account permanently deactivates login credentials (username, password hash), terminates all active authentication sessions, removes profile pictures, and revokes mobile application access.',
      ]],
      ['2. Data Deleted vs. Statutory Records Retained', [
        'Personal login credentials, authentication tokens, mobile push identifiers, and optional profile data will be permanently erased or anonymized upon account deletion.',
        'In compliance with the Maharashtra Village Panchayats Act, Public Records Act, financial accounting rules, and CERT-In cyber regulations, statutory municipal transactions (including issued certificates, registered complaints, property tax demands, payment receipts, and audit trail logs) will be retained in official government archives for the legally mandated statutory retention period.',
      ]],
      ['3. Web Deletion Request Procedure and Timeline', [
        'Citizens without active mobile access may submit a deletion request using the form below. Requests are verified against registered contact details and processed within 15 working days pursuant to the Digital Personal Data Protection (DPDP) Act, 2023.',
      ]],
    ],
  },
  cookies: {
    title: 'Cookies and Local Storage Policy',
    summary: 'Technologies used to keep the portal secure and functional.',
    sections: [
      ['Essential technologies', [
        'The portal may use session identifiers, browser storage, and similar technologies required for authentication, tenant selection, language preferences, security, fraud prevention, accessibility preferences, and continuity of a transaction.',
        'Essential storage cannot always be disabled while using authenticated services. Signing out and clearing browser data removes many device-side values, but does not erase official server records.',
      ]],
      ['Analytics and third parties', [
        'Non-essential analytics, advertising, behavioural profiling, or social tracking must not be enabled without a separate, informed choice where consent is required. This portal does not authorise sale of browsing behaviour.',
        'Embedded maps, payment gateways, document viewers, or messaging services may process technical data under their own policies. They should load only when necessary or after an appropriate user action.',
      ]],
      ['Your controls', [
        'Browser settings can block or delete storage, but doing so may prevent login, security validation, payments, or saved preferences. Any future non-essential cookie controls must make refusal as easy as acceptance and must not use pre-ticked consent.',
      ]],
    ],
  },
  accessibility: {
    title: 'Accessibility Statement',
    summary: 'Commitment to equitable access across devices and abilities.',
    sections: [
      ['Commitment', [
        'The service aims to conform to applicable Guidelines for Indian Government Websites and Apps and WCAG Level AA requirements, including keyboard access, screen-reader compatibility, visible focus, sufficient contrast, meaningful labels, text resizing, responsive layouts, accessible documents, and alternatives for non-text content.',
      ]],
      ['Assistance and feedback', [
        'If content, a form, CAPTCHA, document, map, payment flow, or other feature is inaccessible, contact the office shown below and identify the page, problem, assistive technology, and preferred alternate format. Reasonable assistance and an accessible alternative channel should be provided without loss of entitlement.',
      ]],
    ],
  },
  copyright: {
    title: 'Copyright and Content Use Policy',
    summary: 'Permitted reuse and restrictions for portal content.',
    sections: [
      ['Government content', [
        'Unless a page states otherwise, content owned by the identified Gram Panchayat may be reproduced accurately for lawful, non-misleading purposes after acknowledging the source and current publication date, subject to applicable government copyright directions.',
        'Permission does not extend to the State Emblem, official seals, third-party material, personal data, maps, photographs, software, security features, signatures, certificates, or content carrying separate rights or restrictions.',
      ]],
      ['Prohibited presentation', [
        'Content must not be altered, presented out of context, used to imply endorsement, or reproduced in a misleading or objectionable manner. The authoritative online version and official records prevail.',
      ]],
    ],
  },
  hyperlinks: {
    title: 'Hyperlinking and External Website Policy',
    summary: 'Rules for links to and from this portal.',
    sections: [
      ['External links', [
        'Links to external government or private services are provided for convenience or service delivery. The Gram Panchayat does not control their availability, content, security, accessibility, or privacy practices. A new domain is subject to that operator’s policies.',
      ]],
      ['Links to this portal', [
        'Direct links to public information may generally be created with clear source attribution. Pages must not be framed, mirrored, altered, or presented as another organisation’s content. Links must not imply endorsement or bypass authentication, access control, payment, or document-security mechanisms.',
      ]],
    ],
  },
  disclaimer: {
    title: 'Disclaimer and Official Record Notice',
    summary: 'Status of online information and limits of reliance.',
    sections: [
      ['Official status', [
        'The portal supports access to government information and services but does not replace legislation, gazette notifications, government resolutions, statutory registers, signed orders, sanctioned plans, or records maintained by the competent authority.',
        'Provisional dashboards, calculators, translations, search results, map locations, and status indicators are informational unless explicitly identified as a digitally signed official record.',
      ]],
      ['No exclusion of public responsibility', [
        'Nothing here excludes a statutory duty, citizen remedy, or liability that cannot lawfully be excluded. Errors should be reported for correction. Users should obtain an official certified copy or written clarification before acting on information with legal, property, tax, licence, benefit, or financial consequences.',
      ]],
    ],
  },
};

function DataDeletionForm({ language, tenantSlug }) {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', otp: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [otpMessage, setOtpMessage] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let timer;
    if (otpCountdown > 0) {
      timer = setTimeout(() => setOtpCountdown(c => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpCountdown]);

  const handleSendOtp = async () => {
    setError(null);
    setOtpMessage(null);
    const cleanPhone = formData.phone.replace(/\D/g, '').slice(-10);
    if (!cleanPhone || !/^\d{10}$/.test(cleanPhone)) {
      setError(language === 'mr' ? 'कृपया १०-अंकी वैध मोबाईल नंबर प्रविष्ट करा.' : 'Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!formData.name.trim()) {
      setError(language === 'mr' ? 'कृपया आधी आपले पूर्ण नाव प्रविष्ट करा.' : 'Please enter your full name first.');
      return;
    }
    try {
      setSendingOtp(true);
      await authAPI.requestDataDeletionOtp({
        phone: cleanPhone,
        name: formData.name.trim(),
      });
      setOtpSent(true);
      setOtpCountdown(60);
      setOtpMessage(language === 'mr'
        ? `✓ ${cleanPhone} या WhatsApp क्रमांकावर ६-अंकी OTP पाठवला आहे.`
        : `✓ 6-digit verification OTP sent to WhatsApp number ${cleanPhone}.`);
    } catch (err) {
      setError(err.response?.data?.error || (language === 'mr' ? 'OTP पाठवता आला नाही. कृपया पुन्हा प्रयत्न करा.' : 'Failed to send OTP. Please try again.'));
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    const cleanPhone = formData.phone.replace(/\D/g, '').slice(-10);
    if (!formData.name.trim() || !/^\d{10}$/.test(cleanPhone)) {
      setError(language === 'mr' 
        ? 'कृपया आपले पूर्ण नाव आणि १०-अंकी नोंदणीकृत WhatsApp मोबाईल नंबर प्रविष्ट करा.' 
        : 'Please enter your full name and a valid 10-digit registered WhatsApp mobile number.');
      return;
    }

    if (!formData.otp || !/^\d{6}$/.test(formData.otp.trim())) {
      setError(language === 'mr' 
        ? 'कृपया WhatsApp वर मिळालेला ६-अंकी OTP प्रविष्ट करा.' 
        : 'Please enter the 6-digit verification OTP received on WhatsApp.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name.trim(),
        phone: cleanPhone,
        email: formData.email.trim() || undefined,
        tenant_slug: tenantSlug || undefined,
        reason: formData.reason.trim() || undefined,
        otp: formData.otp.trim(),
      };
      const res = await authAPI.submitDataDeletionRequest(payload);
      setResult(res.data);
      setFormData({ name: '', phone: '', email: '', otp: '', reason: '' });
      setOtpSent(false);
      setOtpMessage(null);
    } catch (err) {
      setError(err.response?.data?.error || (language === 'mr' ? 'विनंती पाठवताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.' : 'Failed to submit request. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="legal-deletion-card">
      {/* ── 3 STEP-BY-STEP METHODS ACCORDING TO GOOGLE PLAY GUIDELINES ── */}
      <div className="legal-deletion-methods">
        <h4 className="deletion-methods-title">
          {language === 'mr' ? '📌 खाते व वैयक्तिक डेटा हटवण्याच्या उपलब्ध पद्धती (३ पर्याय)' : '📌 Available Methods to Delete Your Account & Data (3 Options)'}
        </h4>

        <div className="deletion-methods-grid">
          {/* Method 1: Mobile App */}
          <div className="deletion-method-card">
            <div className="method-card-badge mobile">
              <span className="badge-icon">📱</span>
              <span>{language === 'mr' ? 'पर्याय १: मोबाईल ॲपद्वारे (तत्काळ)' : 'Option 1: In Mobile App (Instant)'}</span>
            </div>
            <h5>{language === 'mr' ? 'ॲपमधील स्वयंचलित खाते विलोपन' : 'In-App Automated Deletion'}</h5>
            <ol className="deletion-steps-list">
              <li>
                <strong>{language === 'mr' ? '१. ॲप उघडा व लॉगिन करा' : '1. Open App & Log In'}</strong>
                <p>{language === 'mr' ? 'आपल्या मोबाईल ॲपमध्ये पासवर्ड किंवा WhatsApp OTP ने लॉगिन करा.' : 'Log in to the mobile app with your password or WhatsApp OTP.'}</p>
              </li>
              <li>
                <strong>{language === 'mr' ? '२. प्रोफाइल (👤) → सुरक्षा विभागात जा' : '2. Go to Profile (👤) → Security'}</strong>
                <p>{language === 'mr' ? 'तळाशी असलेल्या मेनूमधून प्रोफाइल उघडा आणि "सुरक्षा व गोपनीयता" निवडा.' : 'Tap Profile in the bottom navigation and open "Security & Privacy".'}</p>
              </li>
              <li>
                <strong>{language === 'mr' ? '३. "खाते हटवा" वर टॅप करा' : '3. Tap "Delete Account"'}</strong>
                <p>{language === 'mr' ? 'आपला सध्याचा पासवर्ड टाका आणि पुष्टी करा. खाते तत्काळ कायमस्वरूपी बंद होईल.' : 'Enter your current password to verify identity and confirm permanent deletion.'}</p>
              </li>
            </ol>
            <div className="method-footer-note success">
              ✓ {language === 'mr' ? 'खाते व लॉगिन प्रवेश तत्काळ नष्ट होतो' : 'Instant & permanent account deactivation'}
            </div>
          </div>

          {/* Method 2: Web Portal */}
          <div className="deletion-method-card">
            <div className="method-card-badge web">
              <span className="badge-icon">💻</span>
              <span>{language === 'mr' ? 'पर्याय २: वेब पोर्टलद्वारे (तत्काळ)' : 'Option 2: On Web Portal (Instant)'}</span>
            </div>
            <h5>{language === 'mr' ? 'नागरिक डॅशबोर्ड प्रोफाइलमधून' : 'Via Citizen Dashboard Profile'}</h5>
            <ol className="deletion-steps-list">
              <li>
                <strong>{language === 'mr' ? '१. वेब पोर्टलवर लॉगिन करा' : '1. Log into Citizen Portal'}</strong>
                <p>{language === 'mr' ? 'ग्रामपंचायत वेब पोर्टलवर आपल्या खात्यात लॉगिन करा.' : 'Log into your account on the official Gram Panchayat web portal.'}</p>
              </li>
              <li>
                <strong>{language === 'mr' ? '२. प्रोफाइल (👤) टॅब उघडा' : '2. Open Profile (👤) Tab'}</strong>
                <p>{language === 'mr' ? 'नागरिक डॅशबोर्डमध्ये "👤 प्रोफाइल" टॅब निवडा व खाली स्क्रोल करा.' : 'Select the Profile tab in the citizen dashboard and scroll down.'}</p>
              </li>
              <li>
                <strong>{language === 'mr' ? '३. "खाते कायमस्वरूपी हटवा" निवडा' : '3. Click "Delete Account"'}</strong>
                <p>{language === 'mr' ? '"🗑️ खाते हटवा" बटणावर क्लिक करून पासवर्ड टाका व पुष्टी करा.' : 'Click "🗑️ Delete Account" in the Danger Zone, enter password & confirm.'}</p>
              </li>
            </ol>
            <div className="method-footer-note success">
              ✓ {language === 'mr' ? 'वेबवरून तत्काळ सत्र व खाते बंद' : 'Immediate web session termination & deletion'}
            </div>
          </div>

          {/* Method 3: Public Web Request Form */}
          <div className="deletion-method-card active-form-card">
            <div className="method-card-badge form">
              <span className="badge-icon">🌐</span>
              <span>{language === 'mr' ? 'पर्याय ३: अधिकृत ऑनलाइन विनंती फॉर्म' : 'Option 3: Official Web Request Form'}</span>
            </div>
            <h5>{language === 'mr' ? 'ॲप अनइन्स्टॉल असल्यास / लॉगिन नसताना' : 'If App is Uninstalled / No Login'}</h5>
            <ol className="deletion-steps-list">
              <li>
                <strong>{language === 'mr' ? '१. खालील अधिकृत फॉर्म भरा' : '1. Fill the Official Form Below'}</strong>
                <p>{language === 'mr' ? 'आपले पूर्ण नाव, नोंदणीकृत मोबाईल किंवा ईमेल पत्ता प्रविष्ट करा.' : 'Enter your full name and registered phone number or email.'}</p>
              </li>
              <li>
                <strong>{language === 'mr' ? '२. संदर्भ क्रमांक (Request ID) मिळवा' : '2. Receive Request Reference ID'}</strong>
                <p>{language === 'mr' ? 'फॉर्म सबमिट केल्यावर आपल्याला ट्रॅकिंगसाठी तात्काळ Request ID मिळेल.' : 'Upon submission, a unique tracking Request ID is generated instantly.'}</p>
              </li>
              <li>
                <strong>{language === 'mr' ? '३. प्रशासकीय पडताळणी व विलोपन' : '3. Verification & Processing'}</strong>
                <p>{language === 'mr' ? 'DPDP कायदा २०२३ नुसार १५ कामकाजाच्या दिवसांत खाते हटवले जाईल.' : 'Processed within 15 working days pursuant to DPDP Act, 2023.'}</p>
              </li>
            </ol>
            <div className="method-footer-note info">
              ℹ {language === 'mr' ? 'ॲप पुन्हा डाउनलोड न करता विनंती नोंदवा' : 'No app reinstallation or login required'}
            </div>
          </div>
        </div>
      </div>

      <div className="legal-form-container-box">
        <h3>{language === 'mr' ? 'पर्याय ३: ऑनलाइन खाते व डेटा हटवण्याची विनंती नोंदवा (WhatsApp OTP पडताळणीसह)' : 'Option 3: Submit Online Deletion Request (with WhatsApp OTP Verification)'}</h3>
        <p>{language === 'mr' ? 'अनधिकृत विनंत्या रोखण्यासाठी आणि खाते मालकी पडताळण्यासाठी WhatsApp OTP आवश्यक आहे. फॉर्म भरल्यावर ही विनंती थेट ग्रामपंचायत प्रशासनाकडे नोंदवली जाईल.' : 'To prevent unauthorized requests and verify account ownership, WhatsApp OTP verification is required. Upon submission, this request is formally registered with the Gram Panchayat administration.'}</p>
        
        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        {otpMessage && <div className="alert alert-success" style={{ marginBottom: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '10px 14px', borderRadius: 8 }}>{otpMessage}</div>}
        
        {result ? (
          <div className="legal-success-banner">
            <strong>✓ {language === 'mr' ? 'विनंती यशस्वीरीत्या नोंदवली गेली!' : 'Request Registered Successfully!'}</strong>
            <p>{result.message}</p>
            <p style={{ marginTop: 8, fontSize: '0.88rem' }}>
              <strong>{language === 'mr' ? 'संदर्भ क्रमांक (Request ID):' : 'Request Reference ID:'}</strong> <code>{result.request_id}</code>
            </p>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div className="legal-form-group">
            <label>{language === 'mr' ? 'पूर्ण नाव *' : 'Full Name *'}</label>
            <input
              type="text"
              className="legal-form-input"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={language === 'mr' ? 'उदा. राहुल रमेश पाटील' : 'e.g. Ramesh Patil'}
            />
          </div>

          <div className="legal-form-group">
            <label>{language === 'mr' ? 'नोंदणीकृत WhatsApp मोबाईल क्रमांक *' : 'Registered WhatsApp Mobile Number *'}</label>
            <div className="legal-phone-otp-row">
              <input
                type="tel"
                className="legal-form-input"
                required
                maxLength={10}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                placeholder={language === 'mr' ? 'उदा. 9876543210' : 'e.g. 9876543210'}
              />
              <button
                type="button"
                className="legal-send-otp-btn"
                onClick={handleSendOtp}
                disabled={sendingOtp || otpCountdown > 0 || !formData.phone || formData.phone.length < 10}
              >
                {sendingOtp
                  ? (language === 'mr' ? 'पाठवत आहे...' : 'Sending...')
                  : otpCountdown > 0
                    ? (language === 'mr' ? `पुन्हा पाठवा (${otpCountdown}s)` : `Resend (${otpCountdown}s)`)
                    : otpSent
                      ? (language === 'mr' ? 'पुन्हा OTP पाठवा' : 'Resend OTP')
                      : (language === 'mr' ? 'WhatsApp OTP पाठवा' : 'Send WhatsApp OTP')}
              </button>
            </div>
            <small style={{ display: 'block', marginTop: 4, color: '#64748b', fontSize: '0.78rem' }}>
              {language === 'mr' ? 'खाते मालकी पडताळणीसाठी या क्रमांकावर ६-अंकी OTP पाठवला जाईल.' : 'A 6-digit verification code will be sent to this WhatsApp number.'}
            </small>
          </div>

          <div className="legal-form-group">
            <label>{language === 'mr' ? 'WhatsApp OTP (६-अंकी पडताळणी कोड) *' : 'WhatsApp OTP (6-Digit Code) *'}</label>
            <input
              type="text"
              className="legal-form-input"
              required
              maxLength={6}
              value={formData.otp}
              onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              placeholder={language === 'mr' ? 'उदा. 123456' : 'e.g. 123456'}
            />
          </div>

          <div className="legal-form-group">
            <label>{language === 'mr' ? 'ईमेल पत्ता (ऐच्छिक)' : 'Email Address (Optional)'}</label>
            <input
              type="email"
              className="legal-form-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={language === 'mr' ? 'उदा. user@example.com' : 'e.g. user@example.com'}
            />
          </div>

          <div className="legal-form-group">
            <label>{language === 'mr' ? 'खाते हटवण्याचे कारण (ऐच्छिक)' : 'Reason for Deletion (Optional)'}</label>
            <textarea
              className="legal-form-textarea"
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder={language === 'mr' ? 'उदा. गाव सोडले आहे / इतर कारण...' : 'e.g. Moved out of village / no longer required...'}
            />
          </div>

          <button type="submit" className="legal-submit-btn" disabled={loading || !formData.otp || formData.otp.length !== 6}>
            {loading ? (language === 'mr' ? 'पडताळणी होत आहे...' : 'Verifying...') : (language === 'mr' ? 'OTP पडताळा व खाते हटवण्याची विनंती पाठवा' : 'Verify OTP & Submit Deletion Request')}
          </button>
        </form>
      )}
      </div>
    </section>
  );
}

export const legalPath = (tenantSlug, policy) =>
  tenantSlug ? `/gp/${tenantSlug}/legal/${policy}` : `/legal/${policy}`;

export default function LegalPage({ policy: requestedPolicy }) {
  const { tenantSlug: routeSlug, policy: routePolicy } = useParams();
  const { tenant, tenantData, tenants, selectTenant } = useTenant();
  const [language, setLanguage] = useState(() => localStorage.getItem('legalLanguage') || 'mr');
  const policyKey = requestedPolicy || routePolicy || 'terms';
  const activePolicies = language === 'mr' ? marathiPolicies : policies;
  const document = activePolicies[policyKey] || activePolicies.terms;
  const ui = language === 'mr' ? marathiUi : {
    official: 'Official portal policy',
    version: 'Version',
    effectiveDate: 'Effective date',
    policyOwner: 'Policy owner',
    appliesTo: 'Applies to',
    scope: 'Web portal, mobile applications, authenticated services and connected workflows',
    portalPolicies: 'Portal policies',
    navLabel: 'Legal policies',
    contactTitle: 'Official contact and grievance channel',
    authority: 'Authority',
    address: 'Office address',
    email: 'Email',
    telephone: 'Telephone',
    defaultContact: 'Available at Gram Panchayat Office',
    openContact: 'Open the official contact page',
    safety: 'Never include passwords, OTPs, or an unmasked Aadhaar number in an ordinary email or grievance.',
    frameworkTitle: 'Legal and governance framework considered',
    framework: 'Digital Personal Data Protection Act, 2023 and notified Rules; Information Technology Act, 2000 and applicable rules/directions; Right to Information Act, 2005; applicable Aadhaar requirements; Indian Contract Act, 1872; government record-retention and financial rules; Rights of Persons with Disabilities Act, 2016; and Guidelines for Indian Government Websites and Apps.',
    advice: 'This page provides portal policy information and is not a substitute for legislation or legal advice.',
  };

  const activeTenant = useMemo(() => {
    if (tenantData?.tenant) return tenantData.tenant;
    if (tenant) return tenant;
    if (routeSlug && Array.isArray(tenants) && tenants.length > 0) {
      const match = tenants.find(t => t.slug === routeSlug);
      if (match) return match;
    }
    if (Array.isArray(tenants) && tenants.length > 0) {
      return tenants.length === 1 ? tenants[0] : (tenants.find(t => t.slug === 'deulgaon-gada') || tenants[0]);
    }
    return null;
  }, [tenantData, tenant, tenants, routeSlug]);

  const effectiveSlug = routeSlug || activeTenant?.slug || (Array.isArray(tenants) && tenants.length > 0 ? tenants[0].slug : 'deulgaon-gada');

  useEffect(() => {
    if (effectiveSlug && selectTenant && !tenantData?.tenant) {
      selectTenant(effectiveSlug);
    }
  }, [effectiveSlug, selectTenant, tenantData]);

  const owner = activeTenant?.name || (language === 'mr' ? 'ग्रामपंचायत देऊळगाव गाडा' : 'Gram Panchayat Deulgaon Gada');
  const contactEmail = activeTenant?.contact_email || activeTenant?.email || 'deulgaongadagram@gmail.com';
  const contactPhone = activeTenant?.contact_phone || activeTenant?.phone || '+91 20 2740 0000';
  const contactAddress = activeTenant?.address || (activeTenant ? [activeTenant.name, activeTenant.taluka, activeTenant.district, activeTenant.state || 'Maharashtra', activeTenant.pincode].filter(Boolean).join(', ') : (language === 'mr' ? 'ग्रामपंचायत कार्यालय, देऊळगाव गाडा, ता. दौंड, जि. पुणे' : 'Gram Panchayat Office, Deulgaon Gada, Tal. Daund, Dist. Pune'));

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } catch (_) {}
    }
  }, [policyKey]);

  return (
    <main className="legal-page" lang={language === 'mr' ? 'mr' : 'en'}>
      <div className="legal-language-bar" aria-label="Policy language">
        <div className="legal-breadcrumb">
          <span aria-hidden="true">⚖</span>
          <span>{language === 'mr' ? 'कायदेशीर माहिती केंद्र' : 'Legal information centre'}</span>
        </div>
        <span className="legal-language-label">{language === 'mr' ? 'धोरणाची भाषा' : 'Policy language'}</span>
        <div role="group" aria-label="Select language">
          <button type="button" className={language === 'mr' ? 'active' : ''} aria-pressed={language === 'mr'} onClick={() => { setLanguage('mr'); localStorage.setItem('legalLanguage', 'mr'); }}>मराठी</button>
          <button type="button" className={language === 'en' ? 'active' : ''} aria-pressed={language === 'en'} onClick={() => { setLanguage('en'); localStorage.setItem('legalLanguage', 'en'); }}>English</button>
        </div>
      </div>
      <header className="legal-hero">
        <div className="legal-hero-main">
          <div className="legal-document-mark" aria-hidden="true"><span>§</span></div>
          <div>
            <p>{ui.official} · {ui.version} {LEGAL_VERSION}</p>
            <h1>{document.title}</h1>
            <span>{document.summary}</span>
          </div>
        </div>
        <dl>
          <div><dt>{ui.effectiveDate}</dt><dd>{language === 'mr' ? '२८ जुलै २०२६' : LEGAL_EFFECTIVE_DATE}</dd></div>
          <div><dt>{ui.policyOwner}</dt><dd>{owner}</dd></div>
          <div><dt>{ui.appliesTo}</dt><dd>{ui.scope}</dd></div>
        </dl>
      </header>

      <div className="legal-layout">
        <nav className="legal-nav" aria-label={ui.navLabel}>
          <strong>{ui.portalPolicies}</strong>
          {Object.entries(activePolicies).map(([key, item], index) => (
            <Link className={key === policyKey ? 'active' : ''} key={key} to={legalPath(routeSlug || effectiveSlug, key)}>
              <span>{String(index + 1).padStart(2, '0')}</span>{item.title}
            </Link>
          ))}
        </nav>

        <article className="legal-document">
          {document.sections.map(([heading, paragraphs]) => (
            <section key={heading}>
              <h2>{heading}</h2>
              {paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
            </section>
          ))}

          {policyKey === 'data-deletion' && (
            <DataDeletionForm language={language} tenantSlug={routeSlug || effectiveSlug} />
          )}

          <section className="legal-contact">
            <h2>{ui.contactTitle}</h2>
            <p><strong>{ui.authority}:</strong> {owner}</p>
            {contactAddress ? <p><strong>{ui.address}:</strong> {contactAddress}</p> : null}
            <p><strong>{ui.email}:</strong> <a href={`mailto:${contactEmail}`}>{contactEmail}</a></p>
            <p><strong>{ui.telephone}:</strong> <a href={phoneHref(contactPhone)}>{contactPhone}</a></p>
            {effectiveSlug && <p><Link to={`/gp/${effectiveSlug}/contact`}>{ui.openContact}</Link></p>}
            <p>{ui.safety}</p>
          </section>

          <footer className="legal-references">
            <strong>{ui.frameworkTitle}</strong>
            <p>{ui.framework}</p>
            <p>{ui.advice}</p>
          </footer>
        </article>
      </div>
    </main>
  );
}
